'use client';

import { Fragment } from 'react';
import { Link } from '~/components/Link';
import { Typography } from '@bgzt/ui';

import { Spacing, Flex } from '~/components';
import { useEnvironmentInfo } from '~/hooks';
import globalStyles from '~/styles/utils.module.css';
import styles from './productInfo.module.css';
import clsx from 'clsx';

type Props = {
  values: InfoValues;
};

export type InfoValues = {
  brand: { id?: number; name: string } | null;
  categories: { id: string; name: string }[];
  condition: string | null;
  size: string | null;
  qty: number | null;
};

export const INFO_LABELS = {
  brand: '브랜드',
  categories: '카테고리',
  condition: '상품상태',
  size: '사이즈',
  qty: '수량',
} as const;

export const ProductInfo = ({ values }: Props) => {
  const { isWebview } = useEnvironmentInfo();

  return (
    <Flex direction="column" justify="center" align="flex-start">
      {values['brand'] !== null && (
        <>
          <Flex align="center" justify="flex-start" gap={16} className={styles.infoItems}>
            <Typography variant="L8" color="gray-500" className={styles['infoItemLabel']}>
              {INFO_LABELS['brand']}
            </Typography>

            <Typography
              variant="L8"
              color="gray-800"
              className={clsx(styles['infoItemValue'], isWebview && globalStyles['linkText'])}
            >
              {isWebview ? (
                <Link href={`/search/result?q=${values['brand']['name']}`}>{values['brand']['name']}</Link>
              ) : (
                values['brand']['name']
              )}
            </Typography>
          </Flex>

          <Spacing height={12} />
        </>
      )}

      {values['categories'] !== null && (
        <>
          <Flex align="flex-start" justify="flex-start" gap={16} className={styles.infoItems}>
            <Typography variant="L8" color="gray-500" className={styles['infoItemLabel']}>
              {INFO_LABELS['categories']}
            </Typography>

            <Flex style={{ display: 'inline-flex', flexWrap: 'wrap' }}>
              {values['categories']?.map(({ id, name }, index) => {
                return (
                  <Fragment key={`${id}`}>
                    <Typography variant="L8" color="gray-800" className={clsx(isWebview && globalStyles['linkText'])}>
                      {isWebview ? <Link href={`/category/${id}`}>{name}</Link> : name}
                    </Typography>

                    {values['categories']?.length > 1 && (
                      <>
                        {index < values['categories']?.length - 1 && (
                          <Typography
                            variant="C3"
                            color="gray-150"
                            className={clsx(styles['infoItemValue'], styles['arrow'])}
                          >
                            〉
                          </Typography>
                        )}
                      </>
                    )}
                  </Fragment>
                );
              })}
            </Flex>
          </Flex>

          <Spacing height={12} />
        </>
      )}

      {values['condition'] !== null && (
        <>
          <Flex align="center" justify="flex-start" gap={16} className={styles.infoItems}>
            <Typography variant="L8" color="gray-500" className={styles['infoItemLabel']}>
              {INFO_LABELS['condition']}
            </Typography>

            <Typography variant="L8" color="gray-800" className={styles['infoItemValue']}>
              {values['condition']}
            </Typography>
          </Flex>

          <Spacing height={12} />
        </>
      )}

      {values['size'] !== null && (
        <>
          <Flex align="center" justify="flex-start" gap={16} className={styles.infoItems}>
            <Typography variant="L8" color="gray-500" className={styles['infoItemLabel']}>
              {INFO_LABELS['size']}
            </Typography>

            <Typography variant="L8" color="gray-800" className={styles['infoItemValue']}>
              {values['size']}
            </Typography>
          </Flex>

          <Spacing height={12} />
        </>
      )}

      {values['qty'] !== null && (
        <Flex align="center" justify="flex-start" gap={16} className={styles.infoItems}>
          <Typography variant="L8" color="gray-500" className={styles['infoItemLabel']}>
            {INFO_LABELS['qty']}
          </Typography>

          <Typography variant="L8" color="gray-800" className={styles['infoItemValue']}>
            {values['qty'].toLocaleString()}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
};
